import { useState, useCallback, useRef } from 'react';
import { createDefaultSlide, updateSlideCtas, ensureSlideIds } from '../types/slide';
import { FOREST_BG_URLS } from '../constants/backgrounds';
import { arrayMove } from '@dnd-kit/sortable';
import { migrateGenerativeConfig } from '../constants/genArt';
import { migrateLayoutNudge } from '../utils/gridSnap';

/**
 * Custom hook for slide management
 * Handles slide CRUD operations and navigation
 * 
 * @returns {Object} Slide state and handlers
 */
export function useSlides() {
  const [slides, _setSlidesRaw] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);

  // Wrapped setter — guarantees every slide has a stable `id` and migrates
  // old generative configs (SVG → Canvas2D). Both operations are idempotent:
  // slides already carrying ids / new-format configs are left untouched.
  const setSlides = useCallback((update) => {
    _setSlidesRaw((prev) => {
      const next = typeof update === 'function' ? update(prev) : update;
      const withIds = ensureSlideIds(next);
      // Migrate old generative configs (idempotent — no-op for new format)
      let migrated = false;
      const result = withIds.map(slide => {
        let changed = false;
        let updated = slide;

        // Migrate old generative config (SVG → Canvas2D)
        if (slide.generative && slide.generative.preset && !slide.generative.type) {
          const newGen = migrateGenerativeConfig(slide.generative);
          if (newGen !== slide.generative) {
            changed = true;
            updated = { ...updated, generative: newGen };
          }
        }

        // Migrate old layoutNudge (group-keyed → template-scoped)
        if (slide.layoutNudge) {
          const templateId = slide.template || 'forest-classic';
          const newNudge = migrateLayoutNudge(slide.layoutNudge, templateId);
          if (newNudge !== slide.layoutNudge) {
            changed = true;
            updated = { ...updated, layoutNudge: newNudge };
          }
        }

        if (changed) {
          migrated = true;
          return updated;
        }
        return slide;
      });
      return migrated ? result : withIds;
    });
  }, []);
  // Dirty flag — only set by user actions, never by server syncs.
  // Gates autoSave so idle browsers never echo-save received data.
  const dirtyRef = useRef(false);
  // Human-readable hint describing the last user action (sent with save → SSE broadcast)
  const changeHintRef = useRef(null);

  // Map slide field keys to user-friendly labels for change hints
  const fieldLabel = (key) => {
    const labels = {
      title: 'title', subtitle: 'subtitle', kicker: 'kicker',
      body: 'body text', bullets: 'bullets', tags: 'tags',
      backgroundUrl: 'background', bgDarken: 'background darken',
      glassBlur: 'glass effect', widgetBlur: 'widget blur',
      widgetOpacity: 'widget transparency', widgetBorder: 'widget border',
      titleFontSize: 'title size', bodyFontSize: 'body size', fontPreset: 'font',
      imageWidget: 'image widget', videoWidget: 'video widget',
      progressWidget: 'progress widget', statsWidget: 'stats widget',
      calloutWidget: 'callout widget',
      cta: 'CTA', ctaButton: 'CTA button',
      footerEnabled: 'footer', kickerEnabled: 'kicker',
      subtitleEnabled: 'subtitle', bodyEnabled: 'body text',
      bulletsEnabled: 'bullets',
    };
    return labels[key] || key;
  };

  // Get active slide
  const activeSlide = slides[activeIndex];

  // Update active slide
  // Only sets dirtyRef when actual field changes are detected.
  // Returns the same array reference if nothing changed → prevents
  // unnecessary re-renders AND phantom auto-saves.
  const handleSlideChange = useCallback(
    (updatedSlide) => {
      setSlides((prev) => {
        const old = prev[activeIndex];
        if (!old) {
          // No previous slide (shouldn't happen, but guard)
          dirtyRef.current = true;
          const next = [...prev];
          next[activeIndex] = updatedSlide;
          return next;
        }

        // Detect which fields actually changed
        const changed = Object.keys(updatedSlide).filter(k => {
          const oldVal = old[k];
          const newVal = updatedSlide[k];
          if (oldVal === newVal) return false;
          // Deep-equal check for objects/arrays (widgets, bullets, etc.)
          if (typeof oldVal === 'object' && typeof newVal === 'object') {
            return JSON.stringify(oldVal) !== JSON.stringify(newVal);
          }
          return true;
        });

        // No actual changes → return same reference (no re-render, no dirty)
        if (changed.length === 0) return prev;

        // Real change detected — set dirty + hint
        dirtyRef.current = true;
        const label = fieldLabel(changed[0]);
        changeHintRef.current = `Slide ${activeIndex + 1} ${label} was edited`;

        const next = [...prev];
        next[activeIndex] = updatedSlide;
        return next;
      });
    },
    [activeIndex]
  );

  // Add new slide — inherits template + fontPreset + background from the currently active slide.
  // When Normies Studio is active, also inherits the full normies config, generative background,
  // and related visual fields so the new slide opens with Normies Studio already active.
  const handleAddSlide = useCallback(() => {
    dirtyRef.current = true;
    setSlides((prev) => {
      const newSlide = createDefaultSlide(prev.length, prev.length + 1, FOREST_BG_URLS.length);
      // Inherit template and fontPreset from active slide (smarter defaults)
      const sourceSlide = prev[activeIndex];
      if (sourceSlide) {
        if (sourceSlide.template) newSlide.template = sourceSlide.template;
        if (sourceSlide.fontPreset !== undefined) newSlide.fontPreset = sourceSlide.fontPreset;
        // Inherit background from active slide
        if (sourceSlide.bgAssetId) newSlide.bgAssetId = sourceSlide.bgAssetId;
        if (sourceSlide.backgroundUrl) newSlide.backgroundUrl = sourceSlide.backgroundUrl;
        if (sourceSlide.bgColor !== undefined) newSlide.bgColor = sourceSlide.bgColor;
        if (sourceSlide.bgRounded !== undefined) newSlide.bgRounded = sourceSlide.bgRounded;
        if (sourceSlide.darkenAmount !== undefined) newSlide.darkenAmount = sourceSlide.darkenAmount;

        // ── Normies Studio inheritance ──
        // When source slide has Normies Studio active, carry over the full config
        // so the new slide opens with Normies Studio already enabled, with the
        // same mode, template, token, hero image, and generative background.
        if (sourceSlide.normies?.enabled) {
          newSlide.normies = JSON.parse(JSON.stringify(sourceSlide.normies));
          // Carry over generative background (Normies configures this)
          if (sourceSlide.generative) {
            newSlide.generative = JSON.parse(JSON.stringify(sourceSlide.generative));
          }
          // Carry over Normies-related background clearing
          if (sourceSlide.bgSolid !== undefined) newSlide.bgSolid = sourceSlide.bgSolid;
          if (sourceSlide.gradient) newSlide.gradient = JSON.parse(JSON.stringify(sourceSlide.gradient));
          // Carry over text palette
          if (sourceSlide.palette) newSlide.palette = sourceSlide.palette;
        }
      }
      const next = [...prev, newSlide];
      changeHintRef.current = `Slide ${next.length} was added`;
      // Navigate to the new slide
      setActiveIndex(next.length - 1);
      return updateSlideCtas(next);
    });
  }, [activeIndex]);

  // Apply a patch to ALL slides at once (for "All slides" scope: template, font, etc.)
  // `patch` can be:
  //   - An object: applied identically to every slide
  //   - A function(slideIndex): returns a per-slide patch (e.g. sequential seeds)
  const handleBulkSlideChange = useCallback((patch, hint) => {
    dirtyRef.current = true;
    changeHintRef.current = hint || 'All slides were updated';
    if (typeof patch === 'function') {
      setSlides(prev => prev.map((s, i) => ({ ...s, ...patch(i, s) })));
    } else {
      setSlides(prev => prev.map(s => ({ ...s, ...patch })));
    }
  }, []);


  // Remove a slide by index (confirmation handled by caller)
  const handleRemoveSlide = useCallback((index) => {
    const idx = index !== undefined ? index : activeIndex;
    if (slides.length <= 1) return;
    dirtyRef.current = true;
    setSlides((prev) => {
      if (prev.length <= 1) return prev;
      changeHintRef.current = `Slide ${idx + 1} was removed`;
      const next = prev.filter((_, i) => i !== idx);
      return updateSlideCtas(next);
    });
    setActiveIndex((prev) => {
      const maxIdx = slides.length - 2;
      // If we deleted a slide before or at the active one, shift left
      if (idx <= prev) return Math.max(0, prev - 1);
      return Math.min(prev, maxIdx);
    });
  }, [activeIndex, slides.length]);

  // Duplicate a slide — deep copy inserted right after the source
  const handleDuplicateSlide = useCallback((index) => {
    const idx = index !== undefined ? index : activeIndex;
    dirtyRef.current = true;
    setSlides((prev) => {
      const source = prev[idx];
      if (!source) return prev;
      const clone = JSON.parse(JSON.stringify(source));
      // Give the clone a new unique ID
      clone.id = `slide-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const next = [...prev.slice(0, idx + 1), clone, ...prev.slice(idx + 1)];
      changeHintRef.current = `Slide ${idx + 1} was duplicated`;
      return updateSlideCtas(next);
    });
    // Navigate to the new duplicate
    setActiveIndex(idx + 1);
  }, [activeIndex]);



  // Reorder slides (drag-and-drop)
  const handleReorderSlides = useCallback((oldIndex, newIndex) => {
    if (oldIndex === newIndex) return;
    dirtyRef.current = true;
    changeHintRef.current = `Slide ${oldIndex + 1} was moved to position ${newIndex + 1}`;
    setSlides((prev) => {
      const next = arrayMove(prev, oldIndex, newIndex);
      return updateSlideCtas(next);
    });
    // Follow the moved slide so the user keeps editing the same content
    setActiveIndex(newIndex);
  }, []);

  // Navigate to previous slide
  const handlePrev = useCallback(() => {
    setActiveIndex((prev) => (prev - 1 + slides.length) % slides.length);
  }, [slides.length]);

  // Navigate to next slide
  const handleNext = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % slides.length);
  }, [slides.length]);

  return {
    // State
    slides,
    setSlides,
    activeIndex,
    setActiveIndex,
    activeSlide,
    
    // Handlers
    handleSlideChange,
    handleAddSlide,
    handleRemoveSlide,
    handleDuplicateSlide,
    handleReorderSlides,
    handleBulkSlideChange,
    handlePrev,
    handleNext,
    dirtyRef,
    changeHintRef,
  };
}
